import { useQuery } from "@apollo/client/react";
import type { ColumnDef } from "@tanstack/react-table";
import { LuLock } from "react-icons/lu";

import CoinPriceFormatter from "@/components/shared/coin-price-formatter";
import DesoAvatar from "@/components/shared/deso-avatar";
import { UserDisplayNameWithStatus } from "@/components/shared/user-display-name-with-status";
import {
  AccountUsernameByPublicKeyDocument,
  type TradingTopTradersByTokenByVolume,
} from "@/graphql/codegen/graphql";
import { FOCUS_TOKEN_DISPLAY_NAME } from "@/utils/constants";
import getContentBlurredStyle from "@/utils/get-content-blurred-style";
import { abbreviateNumber } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import { getUserName } from "@/utils/usernames";

import { renderSortingHeader } from "../table-sorting-headers";

const TraderTokenContent = ({
  publicKey,
  traderProfilePicUrl,
  username,
  isTrader = false,
  tokenCategory,
  displayName,
  displayNameWrapperClassName,
  displayNameClassName,
}: {
  publicKey?: string;
  username?: string;
  traderProfilePicUrl?: string;
  isTrader?: boolean;
  displayName?: string;
  tokenCategory?: string;
  displayNameWrapperClassName?: string;
  displayNameClassName?: string;
}) => {
  const { data: accountUsernamePKData, loading: accountUsernamePKDataLoading } =
    useQuery(AccountUsernameByPublicKeyDocument, {
      variables: {
        publicKey: publicKey ?? "",
      },
      skip: !!username && !!displayName,
    });

  const finalUserName =
    username ?? accountUsernamePKData?.accountByPublicKey?.username;

  const finalDisplayName =
    displayName ??
    accountUsernamePKData?.accountByPublicKey?.extraData?.DisplayName;

  if (accountUsernamePKDataLoading) {
    return (
      <div className="flex items-center gap-2">
        <DesoAvatar account={null} size={isTrader ? 32 : 24} />
        <span className="text-xs text-muted">Loading...</span>
      </div>
    );
  }

  if (!publicKey) {
    return (
      <div className="flex items-center gap-2">
        <DesoAvatar account={null} size={isTrader ? 32 : 24} />
        <span className="text-xs text-muted">Unknown</span>
      </div>
    );
  }

  const extraData = {
    DisplayName: finalDisplayName,
  };

  return (
    <div
      className={cn(`flex items-center gap-2`, displayNameWrapperClassName)}
      style={getContentBlurredStyle(publicKey?.length)}
    >
      <DesoAvatar
        account={{
          publicKey,
          username: finalUserName,
          extraData,
        }}
        imgURL={traderProfilePicUrl ?? ""}
        size={isTrader ? 32 : 24}
      />
      <div className="flex flex-col">
        <UserDisplayNameWithStatus
          {...{
            publicKey,
            username: finalUserName,
            extraData,
          }}
          className={cn(
            `
              grow truncate overflow-hidden text-left font-sans
              underline-offset-4
            `,
            isTrader ? "text-sm" : "text-xs",
            displayNameClassName,
          )}
          whaleSize={16}
        />
        <span className="text-xs text-muted">
          {isTrader ? (
            getUserName({
              customPrefix: "$",
              account: {
                username: finalUserName,
                publicKey,
                extraData,
              },
            })
          ) : (
            <span>{tokenCategory}</span>
          )}
        </span>
      </div>
    </div>
  );
};

export const columns = (): ColumnDef<TradingTopTradersByTokenByVolume>[] => {
  return [
    {
      accessorKey: "traderPublicKey",
      header: () => <span className="text-xxs">Trader</span>,
      cell: ({ row }) => {
        const traderPK = row.original.traderPublicKey ?? "";
        const traderUsername = row.original.traderUsername ?? "";
        const traderProfilePicUrl = row.original.traderProfilePicUrl ?? "";
        const traderDisplayName = row.original.traderDisplayName ?? "";

        return (
          <TraderTokenContent
            {...{
              publicKey: traderPK,
              traderProfilePicUrl,
              username: traderUsername,
              isTrader: true,
              displayName: traderDisplayName,
              displayNameClassName: "w-[200px]",
              displayNameWrapperClassName: "w-[200px]",
            }}
          />
        );
      },
    },
    {
      accessorKey: "tokenPublicKey",
      header: () => <span className="text-xxs">Token</span>,
      cell: ({ row }) => {
        const traderPK = row.original.tokenPublicKey ?? "";
        const tokenUsername = row.original.tokenUsername ?? "";
        const traderProfilePicUrl = row.original.tokenProfilePicUrl ?? "";
        const tokenCategory = row.original.tokenCategory ?? "";

        return (
          <TraderTokenContent
            {...{
              publicKey: traderPK,
              traderProfilePicUrl,
              username: tokenUsername,
              tokenCategory,
              displayNameClassName: "w-[160px] underline-offset-4 leading-5",
              displayNameWrapperClassName: "w-[160px]",
            }}
          />
        );
      },
    },
    {
      accessorKey: "traderTotalBalanceUsd",
      header: () => <span className="text-xxs">Value</span>,
      cell: ({ row }) => {
        const traderLockedTokenBalanceUsd =
          Number(row.original.traderLockedTokenBalanceUsd) || 0;
        return (
          <div className="flex items-center justify-start gap-1">
            <div
              className="
                flex flex-col gap-0 text-left font-mono text-xs text-foreground
              "
            >
              <CoinPriceFormatter
                price={Number(row.original.traderTokenBalanceUsd) || 0}
                highPriceThreshold={10}
              />

              {traderLockedTokenBalanceUsd > 0 && (
                <div
                  className="
                    flex items-center gap-1 text-[10px] text-muted
                    hover:text-foreground
                  "
                >
                  <LuLock />
                  <CoinPriceFormatter price={traderLockedTokenBalanceUsd} />
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "totalVolumeUsd",
      header: ({ column }) =>
        renderSortingHeader({
          column,
          title: "Trade Volume",
          className: "text-xxs",
        }),
      cell: ({ row }) => (
        <div className="flex items-center justify-start gap-1">
          <div
            className="
              flex flex-col gap-0 text-left font-mono text-xs text-foreground
            "
          >
            <CoinPriceFormatter
              price={row.original.totalVolumeUsd || 0}
              highPriceThreshold={10}
              className="text-emerald-400"
            />
            <div
              className="
                text-[10px] text-muted
                hover:text-foreground
              "
            >
              {abbreviateNumber(row.original.totalVolumeFocus || 0)}{" "}
              {FOCUS_TOKEN_DISPLAY_NAME}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "traderTokenBalance",
      header: () => <span className="text-xxs">Balance</span>,
      cell: ({ row }) => {
        const traderLockedTokenBalance =
          Number(row.original.traderLockedTokenBalance) || 0;
        return (
          <div className="flex items-center justify-start gap-1">
            <div
              className="
                flex flex-col gap-0 text-left font-mono text-xs text-foreground
              "
            >
              <CoinPriceFormatter
                price={Number(row.original.traderTokenBalance) || 0}
                isUsd={false}
              />

              {traderLockedTokenBalance > 0 && (
                <div
                  className="
                    flex items-center gap-1 text-[10px] text-muted
                    hover:text-foreground
                  "
                >
                  <LuLock />
                  <CoinPriceFormatter
                    price={traderLockedTokenBalance}
                    isUsd={false}
                  />
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "boughtSold",
      header: () => <span className="text-xxs">Bought/Sold</span>,
      cell: () => <span className="text-xs text-muted">Coming Soon!</span>,
    },
    {
      accessorKey: "realizedPnL",
      header: () => <span className="text-xxs">Realized PnL</span>,
      cell: () => <span className="text-xs text-muted">Coming Soon!</span>,
    },
    {
      accessorKey: "unrealizedPnL",
      header: () => <span className="text-xxs">Unrealized PnL</span>,
      cell: () => <span className="text-xs text-muted">Coming Soon!</span>,
    },
  ];
};
